import Header from "@/components/header";
import { Outlet, useLocation } from "react-router-dom";
import { Toaster } from 'react-hot-toast';
import { useUser } from "@clerk/clerk-react";
import { Sidebar } from "../pages/sidebar/sidebar";
import { MobileNav } from "../pages/sidebar/mobile-nav";
import { useEffect, useState, useRef, useMemo } from "react";
import toast from 'react-hot-toast';

const AppLayout = () => {
  const { isSignedIn, isLoaded, user, reload } = useUser();
  const location = useLocation();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshAttempts = useRef(0);
  const lastRefreshTime = useRef(0);
  const hasShownToast = useRef(false);
  const hasRunInitialRefresh = useRef(false);

  // Effect to refresh user data when returning from Stripe
  useEffect(() => {
    // Only run this effect for specific paths that need post-payment refresh
    if (location.pathname !== '/success' && location.pathname !== '/dog-analyzer') {
      return;
    }
    
    const refreshUserData = async () => {
      // Prevent multiple refreshes in quick succession (within 2 seconds)
      const now = Date.now();
      if (now - lastRefreshTime.current < 2000) {
        return;
      }
      
      // Check if we're returning from a payment flow or on the success page
      const justPaid = sessionStorage.getItem('justCompletedPayment');
      const redirectTimestamp = sessionStorage.getItem('redirectTimestamp');
      const redirectTime = redirectTimestamp ? parseInt(redirectTimestamp, 10) : 0;
      const isRecentRedirect = now - redirectTime < 10000; // Within 10 seconds
      
      const isSuccessPage = location.pathname === '/success';
      const isDogAnalyzerPage = location.pathname === '/dog-analyzer';
      
    
      
      if ((justPaid === 'true' || isSuccessPage || isRecentRedirect) && 
          isLoaded && user && !isRefreshing && (isDogAnalyzerPage || isSuccessPage)) {
        try {
          setIsRefreshing(true);
          lastRefreshTime.current = now;
          
          await reload();
          
          // Show success toast if coming from success page
          if ((isRecentRedirect || justPaid === 'true') && isDogAnalyzerPage && !hasShownToast.current) {
            hasShownToast.current = true;
            toast.success('¡Tu plan ha sido actualizado!', {
              duration: 5000,
              style: {
                background: '#F0FDF4',
                color: '#166534',
                padding: '16px',
                borderRadius: '8px',
              }
            });
            
            // Clear the flags after showing toast
            if (!isSuccessPage) {
              sessionStorage.removeItem('justCompletedPayment');
              sessionStorage.removeItem('redirectTimestamp');
            }
          }
          
          refreshAttempts.current = 0;
        } catch (error) {
          
          // Retry logic
          refreshAttempts.current += 1;
          if (refreshAttempts.current < 3) {
            setTimeout(refreshUserData, 1000);
          }
        } finally {
          setIsRefreshing(false);
        }
      }
    };

    // Small delay to ensure navigation is complete
    const timer = setTimeout(() => {
      refreshUserData();
    }, 300);

    return () => clearTimeout(timer);
  }, [isLoaded, user, reload, location.pathname]);

  // Force a refresh when the component mounts - but ONLY ONCE
  useEffect(() => {
    // Check if initial refresh has been done already
    if (isLoaded && user && !hasRunInitialRefresh.current) {
      hasRunInitialRefresh.current = true;
      
      const forceRefresh = async () => {
        try {
          if (typeof reload === 'function') {
            await reload();
          } else {
            await user.reload();
          }
          
          // Check if we need to show a toast for a recent payment
          const justPaid = sessionStorage.getItem('justCompletedPayment');
          const redirectTimestamp = sessionStorage.getItem('redirectTimestamp');
          const now = Date.now();
          const redirectTime = redirectTimestamp ? parseInt(redirectTimestamp, 10) : 0;
          const isRecentRedirect = now - redirectTime < 10000; // Within 10 seconds
          
          if ((justPaid === 'true' || isRecentRedirect) && 
              location.pathname === '/dog-analyzer' && 
              !hasShownToast.current) {
            hasShownToast.current = true;
            toast.success('¡Tu plan ha sido actualizado!', {
              duration: 5000,
              style: {
                background: '#F0FDF4',
                color: '#166534',
                padding: '16px',
                borderRadius: '8px',
              }
            });
            
            // Clear the flags after showing toast
            sessionStorage.removeItem('justCompletedPayment');
            sessionStorage.removeItem('redirectTimestamp');
          }
        } catch (error) {
          console.error('Error during initial user data refresh:', error);
        }
      };
      
      forceRefresh();
    }
  }, [isLoaded, user, reload, location]); // Include location but not location.pathname

  // Memoize the sidebar key to prevent unnecessary remounts
  const sidebarKey = useMemo(() => {
    const isPremium = user?.unsafeMetadata?.isPremium === true;
    return `${user?.id || 'guest'}-${isPremium ? 'premium' : 'free'}`;
  }, [user?.id, user?.unsafeMetadata?.isPremium]);

  if (!isLoaded) {
    return null;
  }

  if (!isSignedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-amber-50">
        <Header />
        <main className="flex-1">
          <Outlet />
        </main>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3000,
            style: {
              background: '#ffffff',
              color: '#334155',
            },
          }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-amber-50">
      {/* Sidebar with memoized key */}
      <Sidebar key={sidebarKey} />
      
      {/* Main content wrapper with offset for sidebar */}
      <div className="md:ml-[280px] min-h-screen flex flex-col">
        <main className="flex-1 p-6">
          <div className="h-full bg-white rounded-lg shadow-sm">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile Navigation */}
      <MobileNav />
      
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#ffffff',
            color: '#334155',
          },
        }}
      />
    </div>
  );
};

export default AppLayout;